import {
	ResponsiveContainer,
	BarChart,
	Bar,
	XAxis,
	YAxis,
	Tooltip,
	LabelList,
} from 'recharts'
import { useState } from 'react'
import { useDarkMode } from '../../context/DarkModeContext'

export default function TopSitesBar({ data = {}, title }) {
	const { isDarkMode } = useDarkMode()
	const [activeTab, setActiveTab] = useState('facility')

	const tabs = [
		{ key: 'facility', label: 'Facilities' },
		{ key: 'city', label: 'Cities' },
		{ key: 'state', label: 'States' },
	]

	const backgroundColor = isDarkMode ? '#2d333b' : '#ffffff'
	const textColor = isDarkMode ? '#adbac7' : '#000000'
	const borderColor = isDarkMode ? '#444c56' : '#cccccc'
	const barColor = '#465B7A'

	const formatCurrency = value => {
		if (value >= 1000000) {
			return `$${(value / 1000000).toFixed(1)}M`
		}
		if (value >= 1000) {
			return `$${(value / 1000).toFixed(1)}K`
		}
		return `$${value.toFixed(0)}`
	}

	const formatRPM = value => {
		return `$${value.toFixed(2)}`
	}

	const rawData = data[activeTab] || { items: [], totals: {} }

	const activeData = Array.isArray(rawData)
		? { items: rawData, totals: {} }
		: rawData

	const CustomTooltip = ({ active, payload }) => {
		if (active && payload && payload.length) {
			const data = payload[0].payload
			const showLocation = activeTab === 'facility' && data.city && data.state
			return (
				<div
					style={{
						backgroundColor,
						border: 'none',
						borderRadius: '8px',
						boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
						color: textColor,
						padding: '8px 12px',
					}}
				>
					<p style={{ fontWeight: '600', marginBottom: '4px' }}>
						{data.key}
						{showLocation && (
							<span style={{ fontWeight: '400', fontSize: '12px' }}>
								{' '}
								- {data.city}, {data.state}
							</span>
						)}
					</p>
					<p style={{ fontSize: '13px' }}>
						Revenue: {formatCurrency(data.revenue)}
					</p>
					<p style={{ fontSize: '13px' }}>Count: {data.count}</p>
					<p style={{ fontSize: '13px' }}>RPM: {formatRPM(data.rpm)}</p>
				</div>
			)
		}
		return null
	}

	return (
		<div
			className="rounded-lg shadow"
			style={{
				backgroundColor,
				border: `1px solid ${borderColor}`,
			}}
		>
			{title && (
				<div className="p-4 pb-0">
					<h3 className="text-lg font-semibold" style={{ color: textColor }}>
						{title}
					</h3>
				</div>
			)}

			<div className="px-4 border-b border-gray-200 dark:border-gray-700">
				<nav className="-mb-px flex space-x-4">
					{tabs.map(tab => (
						<button
							key={tab.key}
							onClick={() => setActiveTab(tab.key)}
							className={`${
								activeTab === tab.key
									? 'border-blue-500 text-blue-500'
									: 'border-transparent text-gray-500 dark:text-gray-400 hover:border-gray-300'
							} py-2 px-1 border-b-2 text-sm font-medium transition-colors duration-200`}
						>
							{tab.label}
						</button>
					))}
				</nav>
			</div>

			<div className="p-4">
				<ResponsiveContainer width="100%" height={600}>
					<BarChart
						data={activeData.items?.slice(0, 10) || []}
						layout="vertical"
						margin={{ top: 5, right: 50, left: 1, bottom: 5 }}
					>
						<XAxis type="number" hide />
						<YAxis dataKey="key" type="category" hide />
						<Tooltip
							content={<CustomTooltip />}
							cursor={{ fill: 'transparent' }}
						/>
						<Bar
							dataKey="revenue"
							fill={barColor}
							radius={[4, 4, 4, 4]}
							maxBarSize={40}
						>
							<LabelList
								dataKey="key"
								position="insideLeft"
								style={{
									fill: '#ffffff',
									fontSize: '13px',
									fontWeight: '500',
									paddingLeft: '12px',
								}}
							/>
							<LabelList
								dataKey="revenue"
								position="right"
								formatter={formatCurrency}
								style={{
									fill: textColor,
									fontSize: '13px',
									fontWeight: '500',
								}}
							/>
						</Bar>
					</BarChart>
				</ResponsiveContainer>
			</div>
		</div>
	)
}
